import type { ReactNode } from 'react';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal } from '@querodelivery/uni-react';
import styles from './toggle-cloud-confirm-modal.module.scss';

export type ToggleCloudTarget = 'deliverymen' | 'partners';

interface ToggleCloudConfirmModalProps {
  open: boolean;
  cityName: string;
  isActivating: boolean;
  target: ToggleCloudTarget;
  loading?: boolean;
  onClose(): void;
  onCancel(): void;
  onConfirm(): void;
}

const MODAL_COPY: Record<
  ToggleCloudTarget,
  {
    title: (actionVerb: string) => string;
    description: (actionVerb: string, cityName: string) => ReactNode;
  }
> = {
  deliverymen: {
    title: actionVerb =>
      `Você está ${actionVerb} o cadastro nuvem para entregadores.`,
    description: (actionVerb, cityName) => (
      <>
        Você está {actionVerb} a permissão de cadastro de entregadores na nuvem
        em <span className={styles.cityName}>{cityName}</span>. Certeza que
        deseja continuar?
      </>
    ),
  },
  partners: {
    title: actionVerb =>
      `Você está ${actionVerb} o cadastro nuvem para parceiros.`,
    description: (actionVerb, cityName) => (
      <>
        Você está {actionVerb} a operação nuvem para parceiros em{' '}
        <span className={styles.cityName}>{cityName}</span>. Isso irá alterar
        qual banner será exibido no painel do parceiro para cadastro na nuvem,
        bem como o aviso de que o serviço está ou não presente na cidade.
        Certeza que deseja continuar?
      </>
    ),
  },
};

export function ToggleCloudConfirmModal({
  open,
  cityName,
  isActivating,
  target,
  loading = false,
  onClose,
  onCancel,
  onConfirm,
}: ToggleCloudConfirmModalProps) {
  if (!open) {
    return null;
  }

  const actionVerb = isActivating ? 'ativando' : 'desativando';
  const confirmLabel = isActivating ? 'sim, ativar' : 'sim, desativar';
  const copy = MODAL_COPY[target];

  return (
    <Modal isOpen={open} onClose={onClose} width="31.25rem">
      <div className={styles.content}>
        <FontAwesomeIcon icon={faTriangleExclamation} className={styles.icon} />

        <h4 className={styles.title}>{copy.title(actionVerb)}</h4>

        <p className={styles.description}>
          {copy.description(actionVerb, cityName)}
        </p>

        <div className={styles.actions}>
          <Button
            onClick={onConfirm}
            loading={loading}
            disabled={loading}
            size="lg"
          >
            {confirmLabel}
          </Button>

          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            size="lg"
          >
            cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
